import type { FC, ReactNode } from 'react'

import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/sections/section-heading'

interface PageHeroProps {
    eyebrow: ReactNode
    title: ReactNode
    description?: ReactNode
    children?: ReactNode
}

export const PageHero: FC<PageHeroProps> = ({ eyebrow, title, description, children }): ReactNode => {
    return (
        <section id='page-hero' className='relative isolate overflow-hidden bg-ink pt-32 pb-16 text-white sm:pt-36 sm:pb-20'>
            <div
                aria-hidden='true'
                className='blueprint-grid absolute inset-0 -z-10 text-white [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]'
            />

            <div
                aria-hidden='true'
                className='absolute -top-40 left-1/2 -z-10 size-[36rem] -translate-x-1/2 rounded-full bg-primary/30 blur-3xl'
            />

            <Container className='text-center'>
                <Eyebrow tone='inverse' align='center' className='animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none'>
                    {eyebrow}
                </Eyebrow>

                <h1 className='mx-auto mt-4 max-w-4xl text-4xl font-bold tracking-tight animate-in fade-in slide-in-from-bottom-3 duration-700 motion-reduce:animate-none sm:text-5xl lg:text-[3.5rem] lg:leading-[1.08]'>
                    {title}
                </h1>

                {description && (
                    <p className='mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink-muted animate-in fade-in slide-in-from-bottom-3 duration-700 motion-reduce:animate-none sm:text-lg'>
                        {description}
                    </p>
                )}

                {children}
            </Container>
        </section>
    )
}
